import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { ProductsType } from 'src/constants/products';
import { PATH_ONBOARDING } from 'src/constants/spa-routes';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { booleanScheme, stringEmailScheme, stringScheme } from 'src/utils/validation-schemes';
import { useAppSelector } from 'src/store';
import {
  useDeployConfigurationSettingsMutation,
  useGetAllConfigurationSettingsQuery,
  useGetConfigurationSettingsPromptQuery,
  useGetDefaultConfigurationTemplateFieldsQuery,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import moment from 'moment';
import * as yup from 'yup';
import { EscalatedList } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep6/EscalatedList/EscalatedList';
import { getWorkflowRequestAction } from 'src/pages/ConfigurationPage/WorkflowsTab/getWorkflowRequestAction';
import { validateWorkflowActions } from 'src/helpers/validateWorkflowActions';

const OnboardingStep6 = () => {
  const [activeSettings, setActiveSettings] = useState(null);
  const [settingsPage, setSettingsPage] = useState(1);
  const [settings, setSettings] = useState([]);
  const [inputData, setInputData] = useState(null);
  const [formFields, setFormFields] = useState(null);
  const [formFieldsObject, setFormFieldsObject] = useState({});
  const loader = useLoader();
  const [workflow, setWorkflow] = useState(inputData?.pf_default_workflow || null);
  const { project } = useAppSelector((s) => s.onboardingSlice);
  const { data: defaultTemplateFields, isLoading: isLoadingDefaultTemplateFields } = (
    useGetDefaultConfigurationTemplateFieldsQuery as any
  )();
  const { data: allConfigurationSettings, isLoading: isLoadingAllConfigurationSettings } =
    useGetAllConfigurationSettingsQuery(
      {
        id: project.id,
        page: settingsPage,
      },
      { skip: !Boolean(project.id && defaultTemplateFields) },
    );
  const [
    updateConfigurationSettings,
    {
      data: updateConfigurationSettingsData,
      isLoading: isLoadingUpdateConfigurationSettings,
      isSuccess: isSuccessUpdateConfigurationSettings,
    },
  ] = useUpdateConfigurationSettingsPromptMutation();
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: project.id, settings_id: activeSettings && activeSettings.id },
      { skip: !Boolean(project.id && activeSettings && defaultTemplateFields) },
    );
  const [
    deployConfigurationSettings,
    {
      isLoading: isLoadingDeployConfigurationSettings,
      isSuccess: isSuccessDeployConfigurationSettings,
    },
  ] = useDeployConfigurationSettingsMutation();

  const navigate = useNavigate();

  useEffect(() => {
    if (formFields) {
      const fieldsObject = formFields.reduce((prev, current) => {
        prev[current.key] = current;

        return prev;
      }, {});

      setFormFieldsObject(fieldsObject);
    }
  }, [formFields]);

  useEffect(() => {
    if (allConfigurationSettings) {
      setSettings((prevState) => {
        const newState = [...prevState];

        (allConfigurationSettings as any).items.forEach((item) => {
          const index = prevState.findIndex((prevItem) => {
            return prevItem.id === item.id;
          });

          if (index > -1) {
            newState[index] = item;
          } else {
            newState.push(item);
          }
        });

        newState.sort((a, b) => +moment(b.created_at) - +moment(a.created_at));

        return newState;
      });

      if ((allConfigurationSettings as any).page_info.has_next_page) {
        setSettingsPage((allConfigurationSettings as any).page_info.page + 1);
      }
    }
  }, [allConfigurationSettings]);

  useEffect(() => {
    if (defaultTemplateFields) {
      if (!activeSettings && settings.length) {
        setActiveSettings(settings[0]);
      }

      if (activeSettings) {
        const newActiveSettings = settings.find((item) => {
          return item.id === activeSettings.id;
        });

        setActiveSettings(newActiveSettings);
      } else {
        const fields = (defaultTemplateFields as any).form_fields.map((field, index) => ({
          ...field,
          index,
        }));
        setFormFields(fields);
        setInputData(null);
      }
    }
  }, [settings, defaultTemplateFields]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      const fields = (configurationSettingsPrompt as any).form_fields.map((field, index) => ({
        ...field,
        index,
      }));

      setFormFields(fields);
      setInputData((configurationSettingsPrompt as any).input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (isSuccessUpdateConfigurationSettings) {
      deployConfigurationSettings({ project_id: project.id, settings_id: activeSettings.id });
    }
  }, [isSuccessUpdateConfigurationSettings]);

  useEffect(() => {
    if (isSuccessDeployConfigurationSettings) {
      next();
    }
  }, [isSuccessDeployConfigurationSettings]);

  useEffect(() => {
    if (
      isLoadingDefaultTemplateFields ||
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingUpdateConfigurationSettings ||
      isLoadingDeployConfigurationSettings
    ) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [
    isLoadingDefaultTemplateFields,
    isLoadingAllConfigurationSettings,
    isLoadingConfigurationSettingsPrompt,
    isLoadingUpdateConfigurationSettings,
    isLoadingDeployConfigurationSettings,
  ]);

  useEffect(() => {
    if (inputData) {
      const workflow = inputData?.pf_default_workflow;
      const action = [workflow.action];

      methods.reset({
        name: workflow?.name || 'Escalation',
        description: workflow?.description || 'When AI lacks the knowledge to assist.',
        trigger_type: workflow?.trigger_type || 'when_asked',
        trigger_value: workflow?.trigger_value || '',
        action: action.map((action) => {
          const customActionFields: Record<string, any> = {};
          if (
            action.fields &&
            Boolean(action.fields.filter((field) => field.type === 'custom').length)
          ) {
            customActionFields.action_fields_custom_array = action.fields.filter(
              (field) => field.type === 'custom',
            );
          }

          return {
            action_type:
              action.type &&
              ['collect_and_escalate', 'forward_to_email', 'handle_with_crm', 'close'].includes(
                action.type,
              )
                ? 'escalate'
                : action.type || 'clarify',
            action_fields_custom: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'custom').length,
            ),
            action_fields_name: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'name').length,
            ),
            action_fields_email: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'email').length,
            ),
            action_fields_phone_number: Boolean(
              action.fields &&
              action.fields.filter((field) => field.type === 'phone_number').length,
            ),
            action_message_type: action.message_type || 'transcript',
            message_before: action.message_before || '',
            receiver_email_addresses: action.receiver_email_addresses
              ? action.receiver_email_addresses.join(', ')
              : '',
            sender_name: action.sender_name,
            subject: action.subject,
            sender_email_address: action.sender_email_address,
            message_after: action.message_after || '',
            action_message: action.message || '',
            action_escalated_list: action.type || '',
            action_data_collection: '',
            message_close: action.message || '',
            ...customActionFields,
          };
        }),
      });

      setWorkflow(workflow);
    }
  }, [inputData]);

  useEffect(() => {
    if (updateConfigurationSettingsData) {
      next();
    }
  }, [updateConfigurationSettingsData]);

  const back = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/5?type=${ProductsType.customer}`, { replace: true });
  };

  const next = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/7?type=${ProductsType.customer}`, { replace: true });
  };

  const methods = useForm<any>({
    defaultValues: async () => {
      const action = workflow ? [workflow.action] : [];

      return {
        name: workflow?.name || 'Escalation',
        description: workflow?.description || 'When AI lacks the knowledge to assist.',
        trigger_type: workflow?.trigger_type || 'when_asked',
        trigger_value: workflow?.trigger_value || '',
        action: action.map((action) => {
          const customActionFields: Record<string, any> = {};
          if (
            action.fields &&
            Boolean(action.fields.filter((field) => field.type === 'custom').length)
          ) {
            customActionFields.action_fields_custom_array = action.fields.filter(
              (field) => field.type === 'custom',
            );
          }

          return {
            action_type:
              action.type &&
              ['collect_and_escalate', 'forward_to_email', 'handle_with_crm', 'close'].includes(
                action.type,
              )
                ? 'escalate'
                : action.type || 'clarify',
            action_fields_custom: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'custom').length,
            ),
            action_fields_name: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'name').length,
            ),
            action_fields_email: Boolean(
              action.fields && action.fields.filter((field) => field.type === 'email').length,
            ),
            action_fields_phone_number: Boolean(
              action.fields &&
              action.fields.filter((field) => field.type === 'phone_number').length,
            ),
            action_message_type: action.message_type || '',
            message_before: action.message_before || '',
            receiver_email_addresses: action.receiver_email_addresses
              ? action.receiver_email_addresses.join(', ')
              : '',
            sender_name: action.sender_name,
            subject: action.subject,
            sender_email_address: action.sender_email_address,
            message_after: action.message_after || '',
            action_message: action.message || '',
            action_escalated_list: action.type || '',
            action_data_collection: '',
            message_close: action.message || '',
            ...customActionFields,
          };
        }),
      };
    },
    resolver: yupResolver(
      yup.object({
        name: stringScheme({ required: true }),
        description: stringScheme({ required: true }),
        trigger_type: stringScheme({ required: false }),
        trigger_value: stringScheme({ required: false }),
        action: yup.array(
          yup.object({
            action_type: stringScheme({ required: false }),
            action_message: stringScheme({ required: false }),
            action_escalated_list: stringScheme({ required: false }),
            action_data_collection: stringScheme({ required: false }),
            action_fields_name: booleanScheme({ required: false }),
            action_fields_email: booleanScheme({ required: false }),
            action_fields_phone_number: booleanScheme({ required: false }),
            action_fields_custom: booleanScheme({ required: false }),
            action_message_type: stringScheme({ required: false }),
            message_before: stringScheme({ required: false }),
            receiver_email_addresses: stringEmailScheme({ required: false, format: true }),
            message_after: stringScheme({ required: false }),
            message_close: stringScheme({ required: false }),
            action_fields_custom_array: yup.array(
              yup.object({
                name: stringScheme({ required: false }),
                description: stringScheme({ required: false }),
              }),
            ),
          }),
        ),
      }),
    ),
  });

  const submit = (data) => {
    const is = {
      valid: validateWorkflowActions(data, methods.setError),
      dirty: Boolean(Object.keys(methods.formState.dirtyFields).length),
      deployed: activeSettings && activeSettings.is_deployed,
    };

    if (!is.valid) {
      return;
    }

    if (!is.dirty) {
      return is.deployed
        ? next()
        : deployConfigurationSettings({ project_id: project.id, settings_id: activeSettings.id });
    }

    if (activeSettings) {
      const action = data.action.map((action) => getWorkflowRequestAction(action));
      const newWorkflow = {
        enabled: workflow.enabled,
        type: workflow.type,
        action: action[0],
      };

      const body = {
        input_data: {
          ...inputData,
          pf_default_workflow: newWorkflow,
        },
      };

      updateConfigurationSettings({ project_id: project.id, settings_id: activeSettings.id, body });
    }
  };

  return (
    <div className={'w-full max-w-[650px] h-[65%] max-h-[65%]'}>
      <div className={'flex flex-col gap-[5px] px-[50px] py-1 h-full items-center overflow-y-auto'}>
        <FormProvider {...methods}>
          <form
            id={'onboarding-step-5'}
            className={'flex flex-col gap-5 w-full min-w-[455px] max-w-[455px]'}
            onSubmit={methods.handleSubmit(submit)}
          >
            <EscalatedList
              control={methods.control}
              watch={methods.watch}
              resetField={methods.resetField}
            />
          </form>
        </FormProvider>
      </div>
      <div className={'mt-5 flex items-center justify-center gap-5 px-[50px]'}>
        <div
          className={
            'flex flex-col items-center justify-center gap-5 w-full min-w-[455px] max-w-[455px]'
          }
        >
          <Button form={'onboarding-step-5'} className="w-full py-6 font-semibold">
            <p>Continue</p>
          </Button>
          <div
            onClick={back}
            className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
          >
            ← Back
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep6;
